import { useEffect, useMemo, useRef, useState } from "react";
import { createApolloClient } from "../../providers/ApolloClientFactory";
import { useEventStore } from "../../store/event";
import { GET_EVENT_TXN_AGGREGATE, GET_EVENT_TXN_ROWS } from "./queries";
import {
  reduceInsights,
  EventInsights,
  HourBucket,
  TxnRow,
} from "./insights";

const client = createApolloClient();

// reports_transactions pages transfer fast (~0.4s per 25k skinny rows), but
// browser memory and the reduce keep a ceiling on how much one event pulls.
const PAGE_SIZE = 25000;
const ROW_CAP = 100000;

// ===== Formatters =====
const fmtCents = (cents: number) =>
  `$${(cents / 100).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
const fmtCentsExact = (cents: number) =>
  `$${(cents / 100).toLocaleString("en-US", { minimumFractionDigits: 2 })}`;
const n = (v: number) => v.toLocaleString("en-US");
const pct = (f: number) => `${(f * 100).toFixed(f >= 0.1 ? 0 : 1)}%`;

const Stat = ({ label, value, sub }: { label: string; value: string; sub?: string }) => (
  <div className="flex flex-col rounded-lg border border-gray-200 bg-gray-50/60 px-4 py-3">
    <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
      {label}
    </span>
    <span className="text-xl font-bold tabular-nums text-gray-900">{value}</span>
    {sub && <span className="text-xs text-gray-500">{sub}</span>}
  </div>
);

// Single-series bar chart: one hue, thin rounded-top marks on the baseline,
// 2px gaps, hover readout in ink (not on every bar).
const BarChart = ({
  data,
  minBarPx = 14,
  labelEvery = 1,
}: {
  data: HourBucket[];
  minBarPx?: number;
  labelEvery?: number;
}) => {
  const [hover, setHover] = useState<HourBucket | null>(null);
  const max = Math.max(...data.map((d) => d.revenue), 1);
  if (data.length === 0)
    return <p className="py-6 text-center text-xs text-gray-400">No data</p>;
  return (
    <div>
      <div className="mb-1 h-4 text-xs tabular-nums text-gray-600">
        {hover
          ? `${hover.label} — ${fmtCents(hover.revenue)} · ${n(hover.txns)} sales`
          : " "}
      </div>
      <div className="overflow-x-auto pb-1">
        <div>
          <div className="flex items-end gap-[2px]" style={{ height: 120 }}>
            {data.map((b) => (
              <div
                key={b.key}
                onMouseEnter={() => setHover(b)}
                onMouseLeave={() => setHover(null)}
                title={`${b.label}: ${fmtCents(b.revenue)}`}
                className="flex flex-1 cursor-default items-end"
                style={{ minWidth: minBarPx }}
              >
                <div
                  className={`w-full rounded-t-sm transition-colors ${
                    hover?.key === b.key ? "bg-red-800" : "bg-red-700"
                  }`}
                  style={{ height: Math.max(2, (b.revenue / max) * 118) }}
                />
              </div>
            ))}
          </div>
          <div className="mt-1 flex gap-[2px] border-t border-gray-200 pt-1">
            {data.map((b, i) => (
              <div
                key={b.key}
                className="flex-1 truncate text-center text-[10px] text-gray-400"
                style={{ minWidth: minBarPx }}
              >
                {i % labelEvery === 0 ? b.label.split(" ").pop() : ""}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const ShareMeter = ({ share }: { share: number }) => (
  <div className="flex items-center gap-2">
    <div className="h-2 w-24 rounded-full bg-gray-200">
      <div
        className="h-2 rounded-full bg-red-700"
        style={{ width: `${Math.min(100, share * 100)}%`, minWidth: 2 }}
      />
    </div>
    <span className="w-9 text-right text-xs tabular-nums text-gray-500">
      {pct(share)}
    </span>
  </div>
);

type Phase = "idle" | "loading" | "done" | "error";

export const VendorInsightsPage = () => {
  const headerEventId = useEventStore((s) => s.eventId);
  const allEvents = useEventStore((s) => s.allEvents);
  const eventId = headerEventId ? Number(headerEventId) : null;
  const eventName = useMemo(
    () =>
      (allEvents ?? []).find((e: any) => Number(e?.id) === eventId)?.name ?? null,
    [allEvents, eventId]
  );

  const [phase, setPhase] = useState<Phase>("idle");
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<{ loaded: number; total: number } | null>(null);
  const [insights, setInsights] = useState<EventInsights | null>(null);
  const [meta, setMeta] = useState<{
    totalTxns: number;
    rowsUsed: number;
    timezone: string | null;
  } | null>(null);
  const generation = useRef(0);

  useEffect(() => {
    const gen = ++generation.current;
    setInsights(null);
    setMeta(null);
    setError(null);
    setProgress(null);
    if (!eventId) {
      setPhase("idle");
      return;
    }
    setPhase("loading");
    (async () => {
      try {
        const { data: aggData } = await client.query<{
          reports_transactions_aggregate: {
            aggregate: { count: number } | null;
          };
          events_by_pk: {
            timezone: string | null;
            timezone_diff: number | null;
          } | null;
        }>({
          query: GET_EVENT_TXN_AGGREGATE,
          variables: { eventId },
          fetchPolicy: "network-only",
        });
        if (gen !== generation.current) return;
        const totalTxns =
          aggData.reports_transactions_aggregate.aggregate?.count ?? 0;
        const tzDiff = aggData.events_by_pk?.timezone_diff ?? 0;
        const timezone = aggData.events_by_pk?.timezone ?? null;
        setProgress({ loaded: 0, total: Math.min(totalTxns, ROW_CAP) });

        const rows: TxnRow[] = [];
        for (let offset = 0; offset < Math.min(totalTxns, ROW_CAP); offset += PAGE_SIZE) {
          const { data } = await client.query<{ reports_transactions: TxnRow[] }>({
            query: GET_EVENT_TXN_ROWS,
            variables: { eventId, limit: PAGE_SIZE, offset },
            fetchPolicy: "network-only",
          });
          if (gen !== generation.current) return;
          rows.push(...data.reports_transactions);
          setProgress({ loaded: rows.length, total: Math.min(totalTxns, ROW_CAP) });
          if (data.reports_transactions.length < PAGE_SIZE) break;
        }

        setInsights(reduceInsights(rows, tzDiff));
        setMeta({ totalTxns, rowsUsed: rows.length, timezone });
        setPhase("done");
      } catch (e: any) {
        if (gen !== generation.current) return;
        setError(e?.message ?? "Failed to load insights");
        setPhase("error");
      }
    })();
  }, [eventId]);

  const truncated = meta != null && meta.rowsUsed < meta.totalTxns;

  return (
    <div className="min-h-screen bg-gray-100 p-4">
      {/* Header */}
      <div className="mb-4 rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <h1 className="text-xl font-bold text-gray-900">
          Vendor Insights
          {phase === "loading" && (
            <span className="ml-2 text-xs font-normal text-gray-400">
              {progress
                ? `loading ${n(progress.loaded)} / ${n(progress.total)} transactions…`
                : "sizing…"}
            </span>
          )}
        </h1>
        <p className="text-xs text-gray-500">
          {eventName
            ? `Sales tendencies for ${eventName} — approved transactions only, tips excluded from sales figures`
            : "Select an organization and event in the header"}
          {meta?.timezone && ` · times in ${meta.timezone}`}
        </p>
        {error && (
          <div className="mt-4 rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800">
            {error}
          </div>
        )}
        {truncated && (
          <div className="mt-4 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            Large event: analysis is based on the first {n(meta!.rowsUsed)} of{" "}
            {n(meta!.totalTxns)} transactions (chronological).
          </div>
        )}
      </div>

      {insights && (
        <>
          {/* KPIs */}
          <section className="mb-4 rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
            <div className="grid grid-cols-2 gap-3 md:grid-cols-6">
              <Stat label="Gross sales" value={fmtCents(insights.totals.revenue)} sub="excl. tips" />
              <Stat label="Transactions" value={n(insights.totals.txns)} />
              <Stat
                label="Avg ticket"
                value={fmtCentsExact(insights.totals.avgTicket)}
              />
              <Stat
                label="Items / sale"
                value={insights.totals.itemsPerTxn.toFixed(2)}
              />
              <Stat
                label="Tips"
                value={fmtCents(insights.totals.tip)}
                sub={`${pct(insights.totals.tipRate)} of sales`}
              />
              <Stat
                label="Top tender"
                value={insights.tenderMix[0]?.type ?? "—"}
                sub={insights.tenderMix[0] ? pct(insights.tenderMix[0].share) : undefined}
              />
            </div>
          </section>

          {/* Tendencies */}
          <section className="mb-4 rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
            <h2 className="mb-1 text-base font-semibold text-gray-900">Tendencies</h2>
            <p className="mb-3 text-xs text-gray-500">
              Auto-generated observations from this event's sales patterns.
              Heuristics, not verdicts — verify before acting.
            </p>
            {insights.callouts.length > 0 ? (
              <ul className="space-y-2">
                {insights.callouts.map((c, i) => (
                  <li
                    key={`${c.kind}-${i}`}
                    className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-900"
                  >
                    {c.text}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-xs text-gray-400">
                Nothing stands out for this event.
              </p>
            )}
          </section>

          {/* Sales rhythm */}
          <section className="mb-4 rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
            <h2 className="mb-3 text-base font-semibold text-gray-900">
              Sales by hour of day
              <span className="ml-2 text-xs font-normal text-gray-500">
                all days folded together
              </span>
            </h2>
            <BarChart data={insights.hourOfDay} minBarPx={22} labelEvery={2} />
          </section>

          <section className="mb-4 rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
            <h2 className="mb-3 text-base font-semibold text-gray-900">
              Full timeline
              <span className="ml-2 text-xs font-normal text-gray-500">
                every active hour of the event
              </span>
            </h2>
            <BarChart
              data={insights.timeline}
              minBarPx={insights.timeline.length > 60 ? 8 : 14}
              labelEvery={Math.max(1, Math.ceil(insights.timeline.length / 18))}
            />
          </section>

          {/* Tender mix */}
          <section className="mb-4 rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
            <h2 className="mb-3 text-base font-semibold text-gray-900">Tender mix</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-gray-200 text-left text-gray-500">
                    <th className="px-3 py-1.5 font-medium">Tender</th>
                    <th className="px-3 py-1.5 text-right font-medium">Sales</th>
                    <th className="px-3 py-1.5 text-right font-medium">Transactions</th>
                    <th className="px-3 py-1.5 font-medium">Share</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {insights.tenderMix.map((t) => (
                    <tr key={t.type}>
                      <td className="px-3 py-1.5 font-medium text-gray-900">{t.type}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{fmtCents(t.revenue)}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{n(t.txns)}</td>
                      <td className="px-3 py-1.5"><ShareMeter share={t.share} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Vendor leaderboard */}
          <section className="mb-4 rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
            <h2 className="mb-3 text-base font-semibold text-gray-900">
              Vendors
              <span className="ml-2 text-xs font-normal text-gray-500">
                sorted by sales
              </span>
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-gray-200 text-left text-gray-500">
                    <th className="px-3 py-1.5 font-medium">Vendor</th>
                    <th className="px-3 py-1.5 text-right font-medium">Sales</th>
                    <th className="px-3 py-1.5 font-medium">Share</th>
                    <th className="px-3 py-1.5 text-right font-medium">Txns</th>
                    <th className="px-3 py-1.5 text-right font-medium">Avg ticket</th>
                    <th className="px-3 py-1.5 text-right font-medium">Items/sale</th>
                    <th className="px-3 py-1.5 text-right font-medium">Tip %</th>
                    <th className="px-3 py-1.5 text-right font-medium">Locations</th>
                    <th className="px-3 py-1.5 text-right font-medium">Devices</th>
                    <th className="px-3 py-1.5 font-medium">Peak hour</th>
                    <th className="px-3 py-1.5 text-right font-medium">Peak sales/device-hr</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {insights.vendors.map((v) => (
                    <tr key={v.vendor}>
                      <td className="px-3 py-1.5 font-medium text-gray-900">{v.vendor}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{fmtCents(v.revenue)}</td>
                      <td className="px-3 py-1.5"><ShareMeter share={v.share} /></td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{n(v.txns)}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{fmtCentsExact(v.avgTicket)}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{v.itemsPerTxn.toFixed(2)}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{pct(v.tipRate)}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{n(v.locations)}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{n(v.devices)}</td>
                      <td className="px-3 py-1.5 text-gray-600">{v.peakHourLabel}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">
                        {Math.round(v.peakTxnsPerDeviceHour)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </div>
  );
};
